// Dashboard hero timer (architecture sprint-04 §6.1, S5.4; sprint 7 §7.1). Running/paused only: the idle
// dashboard shows the original Quick actions instead (owner decision 3 supersedes sprint-04 :752).
import { ColorSwatch } from '@/components/ColorSwatch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTimer } from '@/hooks/use-timer';
import { cn } from '@/lib/cn';
import { elapsedSeconds, formatElapsed } from '@/lib/timer-time';
import { useNowTick } from '@/lib/use-now';
import type { TimerSnapshot } from '@/types/timer';
import { useTimerActions } from './TimerActionsProvider';

const TICK_MS = 1000;

export function HeroTimer(): JSX.Element | null {
  const timer = useTimer();
  if (timer.isLoading || !timer.data) return null;
  return <RunningHero snapshot={timer.data} />;
}

function RunningHero({ snapshot }: { snapshot: TimerSnapshot }): JSX.Element {
  const actions = useTimerActions();
  const perfNow = useNowTick(TICK_MS);
  const { timer } = snapshot;
  const paused = timer.pausedAt !== null;
  const project = timer.project;
  const label = (project?.name ?? 'No project') + (timer.description ? ` · ${timer.description}` : '');

  return (
    <section aria-label="Timer" className="card mb-6 border-primary/40 px-4 py-8 text-center">
      <div className="mb-3 flex items-center justify-center gap-2">
        <span aria-hidden="true" className={cn('h-2 w-2 shrink-0 rounded-full', paused ? 'bg-faint' : 'animate-pulse bg-danger')} />
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{paused ? 'Paused' : 'Recording'}</span>
      </div>
      <p role="timer" aria-label="Elapsed time" className="font-mono text-6xl tabular-nums tracking-tight text-foreground sm:text-7xl">
        {formatElapsed(elapsedSeconds(snapshot, perfNow))}
      </p>
      <div className="mb-1 mt-3 flex items-center justify-center gap-2">
        {project ? <ColorSwatch hex={project.color} size={8} /> : <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40" />}
        <span className="truncate text-sm text-muted-foreground">{label}</span>
        {project?.status === 'archived' && <Badge>Archived</Badge>}
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        {paused ? (
          <Button onClick={actions.resume} disabled={actions.pending}>
            Resume
          </Button>
        ) : (
          snapshot.timer.rules?.pauseAllowed !== false && (
            <Button variant="outline" onClick={actions.pause} disabled={actions.pending}>
              Pause
            </Button>
          )
        )}
        {!paused && snapshot.timer.rules?.switchAllowed !== false && (
          <Button variant="outline" onClick={actions.openSwitch} disabled={actions.pending}>
            I switched
          </Button>
        )}
        <Button onClick={actions.requestStop} disabled={actions.pending}>
          Stop
        </Button>
        <Button variant="ghost" className="hover:text-danger-fg" onClick={actions.requestDiscard} disabled={actions.pending}>
          Discard
        </Button>
      </div>
    </section>
  );
}
